import { useUserStore } from '@talon/web/src/context';
import { useOAuthPopup } from '@talon/web/src/hooks/useOAuthPopup';
import { Box, Button, Text } from 'grommet';
import { Google, StatusGood, Trash } from 'grommet-icons';
import { TOAuthProvider } from 'lib/api';
import { observer } from 'mobx-react-lite';
import { toast } from 'react-toastify';

export interface OAuthAccountManagerProps {
  /** Show as compact inline buttons (for import dialogs) */
  compact?: boolean;
  /** Only show specific providers */
  providers?: TOAuthProvider[];
  /** Callback after successful connection */
  onConnect?: (provider: TOAuthProvider) => void;
  /** Callback after successful disconnection */
  onDisconnect?: (provider: TOAuthProvider) => void;
}

const providerConfig = {
  [TOAuthProvider.GOOGLE]: {
    name: 'Google Drive',
    icon: Google,
    color: '#4285F4',
  },
};

export const OAuthAccountManager = observer(
  ({ compact = false, providers, onConnect, onDisconnect }: OAuthAccountManagerProps) => {
    /** Context **/
    const userStore = useUserStore();
    const { openOAuthPopup } = useOAuthPopup();

    /** Computed **/
    const availableProviders = providers || Object.values(TOAuthProvider);

    /** Methods **/

    const handleConnect = async (provider: TOAuthProvider) => {
      try {
        const result = await openOAuthPopup(provider, userStore.token);

        if (result.status === 'success') {
          // Refresh the entire user profile to get updated oauth_profiles
          await userStore.refreshUser();
          toast.success(`Successfully connected to ${providerConfig[provider].name}`);
          onConnect?.(provider);
        } else {
          toast.error(`Failed to connect to ${providerConfig[provider].name}`);
        }
      } catch (error) {
        console.error(`OAuth connection error for ${provider}:`, error);
        toast.error(`Error connecting to ${providerConfig[provider].name}`);
      }
    };

    const handleDisconnect = async (provider: TOAuthProvider) => {
      try {
        await userStore.disconnectOAuthProfile(userStore.user.id, provider);
        toast.success(`Disconnected from ${providerConfig[provider].name}`);
        onDisconnect?.(provider);
      } catch (error) {
        console.error(`OAuth disconnection error for ${provider}:`, error);
        toast.error(`Error disconnecting from ${providerConfig[provider].name}`);
      }
    };

    const isConnected = (provider: TOAuthProvider) =>
      userStore.user.oauth_profiles?.some((profile) => profile.provider === provider);

    const getConnectedProfile = (provider: TOAuthProvider) =>
      userStore.user.oauth_profiles?.find((profile) => profile.provider === provider);

    /** Render **/

    if (compact) {
      // Compact inline view for import buttons
      return (
        <Box direction="row" gap="small" justify="center" wrap>
          {availableProviders.map((provider) => {
            const config = providerConfig[provider];
            const Icon = config.icon;
            const connected = isConnected(provider);
            const profile = getConnectedProfile(provider);

            return (
              <Box key={provider} flex>
                <Button
                  icon={<Icon />}
                  label={connected ? `${config.name} (Connected)` : `Connect ${config.name}`}
                  onClick={() => (connected ? handleDisconnect(provider) : handleConnect(provider))}
                  primary={!connected}
                  secondary={connected}
                  size="small"
                  tip={
                    connected && profile
                      ? `Connected as ${profile.name || profile.email || 'Unknown'}`
                      : `Connect your ${config.name} account`
                  }
                />
              </Box>
            );
          })}
        </Box>
      );
    }

    // Full profile page view
    return (
      <Box gap="small">
        {availableProviders.map((provider) => {
          const config = providerConfig[provider];
          const Icon = config.icon;
          const connected = isConnected(provider);
          const profile = getConnectedProfile(provider);

          return (
            <Box
              key={provider}
              direction="row"
              align="center"
              justify="between"
              pad="small"
              background={connected ? { color: 'light-2' } : 'light-1'}
              round="small"
              border={connected ? { color: 'brand', size: 'small' } : undefined}
            >
              <Box direction="row" align="center" gap="medium">
                <Box
                  pad="small"
                  round="small"
                  background={{ color: config.color, opacity: 0.1 }}
                  style={{ color: config.color }}
                >
                  <Icon size="medium" />
                </Box>
                <Box>
                  <Box direction="row" align="center" gap="small">
                    <Text weight="bold">{config.name}</Text>
                    {connected && <StatusGood size="small" color="status-ok" />}
                  </Box>
                  {connected && profile ? (
                    <Box direction="column" gap="xxsmall">
                      {profile.name && (
                        <Text size="small" color="dark-3">
                          {profile.name}
                        </Text>
                      )}
                      {profile.email && (
                        <Text size="xsmall" color="dark-4">
                          {profile.email}
                        </Text>
                      )}
                    </Box>
                  ) : (
                    <Text size="small" color="dark-4">
                      Not connected
                    </Text>
                  )}
                </Box>
              </Box>
              <Box>
                {connected ? (
                  <Button
                    icon={<Trash size="small" />}
                    label="Disconnect"
                    onClick={() => handleDisconnect(provider)}
                    secondary
                    size="small"
                  />
                ) : (
                  <Button label="Connect" onClick={() => handleConnect(provider)} primary size="small" />
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  }
);
